/* istanbul ignore file */
import { GlobalStyles, ThemeProvider } from '@ori-ui/mui';
import { UniversalTokenProvider, type UniversalTokenProviderProps, doDevelopmentLogin } from '@ori/auth';
import { parseClientAppConfig } from '@ori/client-app-config';
import type { PickClientAppDataConfigProps } from '@ori/client-app-config';
import { ServiceUrlsProvider, type ServiceUrlsProviderProps } from '@ori/configuration-manager';
import { type EnvironmentContext, EnvironmentProvider } from '@ori/environment';
import { TranslationsProvider } from '@ori/i18n';
import { LoggerProvider, ApplicationInsights, getClientApplicationInsightsConfig, ErrorBoundary } from '@ori/logger';
import { PersonalMenu, type PersonalMenuProps, defaultTranslationsWithNamespaces } from '@ori/personal-menu';
import { Team, TestIdProvider, type TestIdProviderProps } from '@ori/testid-generator';
import { type FC, type ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';

import { APP_NAME, APP_ROOT_ELEMENT_ID, EMOTION_CACHE_KEY } from './constants-generated';
import { mainLogger } from './logger';
import { useOutlets } from './useOutlets';

const logger = mainLogger.createChild('appRender');

// Keep the casing accurate here!
type PersonalMenuInitialDataFromDataConfig = {
  CustomerFirstName: string;
  CustomerLastName: string;
  UserType: string;
  UserHash: string;
  Items: Array<{
    Title: string;
    Url: string;
    Icon: string;
    IsViewabled?: boolean;
  }>;
  UseCaching: boolean;
  OrderType: string;
};

const parseTopAreaAppConfig = (config: string) =>
  parseClientAppConfig<
    PickClientAppDataConfigProps<
      | 'currentLanguage'
      | 'customerId'
      | 'globalApiUrl'
      | 'graphqlUrl'
      | 'graphqlNetUrl'
      | 'identityUrl'
      | 'staticApiUrl'
      | 'tenant'
    > & {
      mobileOfficeApiUrl: string;
      personalMenuInitialData?: PersonalMenuInitialDataFromDataConfig;
      consultantNumber?: string;
      consultantType?: number;
    }
  >(config, [
    'currentLanguage',
    'globalApiUrl',
    'graphqlUrl',
    'graphqlNetUrl',
    'mobileOfficeApiUrl',
    'staticApiUrl',
    'tenant',
  ]);

const mapClientAppConfigToPersonalMenuConfiguration = (
  data: ReturnType<typeof parseTopAreaAppConfig>,
): NonNullable<PersonalMenuProps['configuration']> => {
  const { personalMenuInitialData, consultantType, consultantNumber } = data;
  const configuration: NonNullable<PersonalMenuProps['configuration']> = {
    consultantType,
    consultantNumber,
  };

  if (personalMenuInitialData) {
    const {
      CustomerFirstName: customerFirstName,
      CustomerLastName: customerLastName,
      OrderType,
      UseCaching: useCaching,
      UserHash,
      UserType,
    } = personalMenuInitialData;
    type InitialData = NonNullable<NonNullable<PersonalMenuProps['configuration']>['personalMenuInitialData']>;

    configuration.personalMenuInitialData = {
      // Items are not needed here
      items: [],
      orderType: OrderType as InitialData['orderType'],
      useCaching,
      userType: UserType as InitialData['userType'],
      userHash: UserHash,
      customerFirstName,
      customerLastName,
    };
  }

  return configuration;
};

/**
 * React app renderer that is used when app is used as a standalone app
 */
export async function render(rootElement: HTMLElement) {
  const { config = '' } = rootElement.dataset;
  // When rootElement is created it copies configuration from top-area cap root element
  const clientAppConfig = parseTopAreaAppConfig(config);
  const { customerId, identityUrl, tenant, currentLanguage, staticApiUrl } = clientAppConfig;

  // Initialize Application Insights in production
  if (process.env.NODE_ENV === 'production') {
    ApplicationInsights.initialize(getClientApplicationInsightsConfig());
  } else {
    // when using Requestly we do not want to load token
    // eslint-disable-next-line no-lonely-if -- thats okay
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      // eslint-disable-next-line no-console -- wanted behavior
      console.log('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }

      await doDevelopmentLogin({ identityUrl, tenant });
    }
  }

  const environmentProviderProps: EnvironmentContext = {
    tenant,
  };
  const serviceUrlsProviderProps: ServiceUrlsProviderProps = {
    serviceUrls: clientAppConfig,
  };
  const universalTokenProviderProps: UniversalTokenProviderProps = {
    customerId,
    initialToken: '',
  };
  const testIdProviderProps: TestIdProviderProps = {
    team: Team.AcquisitionGrowth,
    project: APP_NAME,
  };
  const translationsProviderProps: Omit<ComponentProps<typeof TranslationsProvider>, 'children'> = {
    currentLanguage,
    defaultTranslationsWithNamespaces,
    clientFetchConfiguration: {
      baseUrl: staticApiUrl,
      onError: (cause: unknown) => logger.error(new Error('Error occured while loading translations', { cause })),
      tenant,
    },
  };

  const personalMenuConfiguration: PersonalMenuProps['configuration'] =
    mapClientAppConfigToPersonalMenuConfiguration(clientAppConfig);

  const ContentComponent: FC = () => {
    const outlets = useOutlets();

    const content = (
      <ThemeProvider cacheKey={EMOTION_CACHE_KEY}>
        <LoggerProvider logger={mainLogger}>
          <ErrorBoundary areaName="appRender">
            <EnvironmentProvider {...environmentProviderProps}>
              <ServiceUrlsProvider {...serviceUrlsProviderProps}>
                <UniversalTokenProvider {...universalTokenProviderProps}>
                  <TestIdProvider {...testIdProviderProps}>
                    <TranslationsProvider {...translationsProviderProps}>
                      <PersonalMenu configuration={personalMenuConfiguration} />
                    </TranslationsProvider>
                  </TestIdProvider>
                </UniversalTokenProvider>
              </ServiceUrlsProvider>
            </EnvironmentProvider>
          </ErrorBoundary>
        </LoggerProvider>
        <GlobalStyles
          // eslint-disable-next-line react/jsx-no-bind -- accessing theme
          styles={(theme) => ({
            [theme.breakpoints.down(1024)]: {
              // Hide sidemenu on mobile as menu is in burger menu on mobile
              [`#${APP_ROOT_ELEMENT_ID}`]: {
                display: 'none',
              },
            },
          })}
        />
      </ThemeProvider>
    );

    return (
      <>
        {content}
        {outlets.map((element) => createPortal(content, element))}
      </>
    );
  };
  createRoot(rootElement).render(<ContentComponent />);
}
